import { GradeQuadro, GradeSemanas } from '@/domain/mapa/interfaces/mapa';

/**
 * O quadrinho da SEMANA DE HOJE de uma célula: é dele que o medidor "hoje" fala.
 *
 * `undefined` quando a carteira não cobre a semana corrente ou quando aquela célula não
 * tem quadrinho nela, e aí o medidor diz que não há capacidade registrada em vez de
 * mostrar zero: "0%" e "ninguém disse" são leituras opostas.
 */
export function quadroDaSemanaAtual(
  grade: GradeSemanas | null,
  celula: string,
): GradeQuadro | undefined {
  const atual = (grade?.semanas ?? []).find((semana) => semana.atual);
  if (atual === undefined) {
    return undefined;
  }
  return (grade?.quadros ?? []).find(
    (quadro) => quadro.semanaInicio === atual.inicio && quadro.celula === celula,
  );
}

/**
 * The hours still free in one square. Never negative: a week that is already over its
 * capacity has nothing left, and "-6h livres" reads as an amount somebody could use.
 * A square with no capacity registered has no answer to give, so it returns zero too.
 */
export function horasLivres(quadro: GradeQuadro): number {
  if (quadro.ocupacaoPct === null) {
    return 0;
  }
  return Math.max(0, quadro.horasCapacidade - quadro.horasProgramadas);
}

/**
 * As horas livres que AINDA existem: do dia de hoje em diante, com os dias já passados
 * fora dos dois lados da conta.
 *
 * `horasLivres` fala da semana INTEIRA, que é a leitura certa para julgar se ela foi bem
 * planejada. Mas "o que ainda cabe" não pode somar a capacidade de segunda numa quarta:
 * numa semana já começada, aquela capacidade não existe mais para ser oferecida.
 *
 * Nas semanas futuras os dois números coincidem, porque nenhum dia passou.
 */
export function horasLivresRestantes(quadro: GradeQuadro): number {
  if (quadro.ocupacaoPct === null) {
    return 0;
  }
  return Math.max(0, quadro.horasCapacidadeRestante - quadro.horasProgramadasRestantes);
}

/**
 * What the current week still has free, across the cells on screen. It answers the one
 * question the grid is opened with ("does anything else fit this week?") without making
 * the person add the squares up by eye.
 *
 * `null` when no square of that week has a capacity registered: a zero there would read
 * as "the week is full", which is the opposite of "nobody said how big it is".
 */
export function horasLivresDaSemana(quadros: ReadonlyArray<GradeQuadro>, semanaInicio: string): number | null {
  const daSemana = quadros.filter((quadro) => quadro.semanaInicio === semanaInicio && quadro.ocupacaoPct !== null);
  if (daSemana.length === 0) {
    return null;
  }
  // RESTANTES, não as da semana cheia: a linha fala do que ainda dá para encaixar, e no
  // domingo 18/10 ela oferecia as 651h da semana de 12/10, que já tinha acabado.
  return daSemana.reduce((total, quadro) => total + horasLivresRestantes(quadro), 0);
}
